import {
  customFieldsListOptions,
  Dropdown,
  EntityTypeEnum,
  LeadsStatusesListData,
  leadsStatusesListListInfiniteOptions,
  leadsStatusesListOptions,
  LeadsTypesListData,
  leadsTypesListInfiniteOptions,
  PaginatedDropdownList,
  PaginatedReturnLeadTypeListReadable,
  ReturnLeadTypeReadable,
  Type83fEnum
} from '@crm/api'
import { CustomField, InfiniteSelect } from '@crm/components'
import { defaultNonPagination } from '@crm/utils'
import { Text } from '@shared/components'
import { useLocales } from '@shared/locales'
import { transformOptions } from '@shared/utils'
import { useQuery } from '@tanstack/react-query'
import { Col, Divider, Form, Input, Row } from 'antd'
import { FormInstance } from 'antd/lib'
import { useEffect } from 'react'

type Props = {
  form: FormInstance
  isEdit?: boolean
  hasIdnp?: boolean
}

export const Lead = ({ form, isEdit, hasIdnp }: Props) => {
  const { t } = useLocales()

  const leadTypeValue = Form.useWatch('lead_type', form)
  const leadTypeCode = Form.useWatch('lead_type_code', form)
  const statusValue = Form.useWatch('status', form)
  const isLegalPerson = leadTypeCode === 'legal_person'

  const { data: statusesData } = useQuery({
    ...leadsStatusesListOptions({
      query: {
        lead_type: leadTypeValue ? [Number(leadTypeValue)] : undefined,
        per_page: 1
      }
    }),
    enabled: !!leadTypeValue
  })

  useEffect(() => {
    if (!isEdit && statusesData?.results?.[0]) {
      form.setFieldValue('status', statusesData.results[0].id)
    }
  }, [statusesData, isEdit, form])

  const { data } = useQuery(
    customFieldsListOptions({
      query: {
        entity_type: [EntityTypeEnum.LEAD],
        ...defaultNonPagination
      }
    })
  )

  const availableCustomFields = data?.results?.filter((e) => !e.deleted_at)

  const { filteredCustomFields, checkboxCustomFields } = Object.groupBy(
    availableCustomFields || [],
    (e) =>
      e.type === Type83fEnum.BOOLEAN
        ? 'checkboxCustomFields'
        : 'filteredCustomFields'
  )

  return (
    <Row gutter={[16, 16]}>
      <Col span={24}>
        <Text size={16} weight={500}>
          {t('lead.info')}
        </Text>
      </Col>

      <Form.Item name="lead_type_code" hidden />

      <Col span={12}>
        <Form.Item
          name="lead_type"
          label={t('contacts.person_type')}
          rules={[{ required: true }]}
        >
          <InfiniteSelect<
            LeadsTypesListData,
            PaginatedReturnLeadTypeListReadable,
            ReturnLeadTypeReadable
          >
            className="select-min-width"
            placeholder={t('contacts.person_type')}
            getQueryFunction={leadsTypesListInfiniteOptions}
            getOptions={({ options }) =>
              transformOptions({
                options,
                label: 'name',
                value: 'id',
                returnOption: true
              })
            }
            onChange={(value, option) => {
              const selected = Array.isArray(option) ? option[0] : option
              form.setFieldValue(
                'lead_type_code',
                (selected as ReturnLeadTypeReadable)?.code
              )
              form.resetFields(['status'])
              form.setFieldValue('lead_type', value)
            }}
          />
        </Form.Item>
      </Col>

      <Col span={12}>
        <Form.Item dependencies={['lead_type']}>
          {() => {
            const pipelineId = form.getFieldValue('lead_type')

            return (
              <Form.Item
                className="w-full"
                dependencies={['lead_type']}
                name="status"
                label={t('contacts.status')}
                rules={[{ required: true }]}
              >
                <InfiniteSelect<
                  LeadsStatusesListData,
                  PaginatedDropdownList,
                  Dropdown
                >
                  placeholder={t('filter.status')}
                  className="select-min-width"
                  getQueryFunction={leadsStatusesListListInfiniteOptions}
                  baseOptions={{
                    query: {
                      lead_type: pipelineId ? Number(pipelineId) : undefined,
                      preference: statusValue ? [statusValue] : undefined
                    }
                  }}
                  disabled={!pipelineId}
                />
              </Form.Item>
            )
          }}
        </Form.Item>
      </Col>

      {isLegalPerson && (
        <Col span={12}>
          <Form.Item
            name="name"
            label={t('contacts.name_lead')}
            rules={[{ required: true }]}
          >
            <Input placeholder={t('contacts.name_lead')} />
          </Form.Item>
        </Col>
      )}

      <Col span={12}>
        <Form.Item
          name="identification_number"
          label={t('identification_number')}
          normalize={(value, prevValue) =>
            String(value).length > 13 ? prevValue : value
          }
          rules={[
            {
              pattern: /^\d{13}$/,
              message: t('invalid_personal_code_format')
            }
          ]}
        >
          <Input
            type="number"
            min={0}
            placeholder={t('identification_number')}
            disabled={hasIdnp}
          />
        </Form.Item>
      </Col>

      {((filteredCustomFields?.length ?? 0) > 0 ||
        (checkboxCustomFields?.length ?? 0) > 0) && (
        <>
          <Divider />

          <Col span={24}>
            <Text size={16} weight={500}>
              {t('nomenclature.custom_fields')}
            </Text>
          </Col>

          {filteredCustomFields?.map((item) => (
            <Col span={12} key={item.id}>
              <CustomField {...item} />
            </Col>
          ))}

          {checkboxCustomFields?.map((item) => (
            <Col span={24} key={item.id}>
              <CustomField {...item} />
            </Col>
          ))}
        </>
      )}
    </Row>
  )
}
